import React, { useEffect, useState } from 'react';
import { ArchitectureNode, ArchitectureEdge, ComponentType, ConnectionType, TextElement } from '../types';
import { CONNECTION_TYPES } from '../constants';
import { X, ArrowRight, ChevronLeft, ChevronRight, Settings2, Trash2, Lock, Unlock, Move, Maximize, MessageSquare, FolderOpen, Layers } from 'lucide-react';

interface PropertiesPanelProps {
  selectedNode: ArchitectureNode | null;
  selectedEdge: ArchitectureEdge | null;
  selectedTextElement: TextElement | null;
  updateNode: (id: string, updates: any) => void;
  updateEdge: (id: string, updates: any) => void;
  updateTextElement: (id: string, updates: Partial<TextElement>) => void;
  onDelete: () => void;
  onDeleteTextElement: () => void;
  onClose: () => void;
  onOpenInside?: (nodeId: string) => void;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ 
  selectedNode, 
  selectedEdge,
  selectedTextElement,
  updateNode, 
  updateEdge,
  updateTextElement,
  onDelete,
  onDeleteTextElement,
  onClose,
  onOpenInside
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // -- LOCAL STATE --
  // We keep local state for inputs to avoid jumping UI if parent updates lag slightly, 
  // but we sync from props in useEffect.
  
  // Node Vars
  const [label, setLabel] = useState('');
  const [specs, setSpecs] = useState<any>({});
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [locked, setLocked] = useState(false);
  const [isCluster, setIsCluster] = useState(false);
  const [instanceCount, setInstanceCount] = useState<number | undefined>(undefined);
  const [comment, setComment] = useState('');
  
  // Edge Vars
  const [edgeType, setEdgeType] = useState<ConnectionType | ''>('');
  const [edgePort, setEdgePort] = useState('');
  const [edgeAnimated, setEdgeAnimated] = useState(true);
  const [edgeComment, setEdgeComment] = useState('');
  const [edgeCustomLabel, setEdgeCustomLabel] = useState('');
  const [edgeLabelVisible, setEdgeLabelVisible] = useState(true);
  const [edgeLabelPosition, setEdgeLabelPosition] = useState<'auto' | 'top' | 'bottom' | 'left' | 'right'>('auto');
  const [edgeCustomColor, setEdgeCustomColor] = useState('');
  const [edgeStrokeWidth, setEdgeStrokeWidth] = useState<'thin' | 'normal' | 'thick'>('normal');
  const [edgeLineStyle, setEdgeLineStyle] = useState<'solid' | 'dashed' | 'dotted'>('solid');
  const [edgeOpacity, setEdgeOpacity] = useState(1);
  const [edgeLatency, setEdgeLatency] = useState('');
  const [edgeBandwidth, setEdgeBandwidth] = useState('');
  const [edgeTags, setEdgeTags] = useState<string[]>([]);
  const [edgeTagInput, setEdgeTagInput] = useState('');
  const [edgeStatus, setEdgeStatus] = useState<'active' | 'inactive' | 'degraded'>('active');

  // Track if we are currently editing an input to avoid upstream overrides
  const [isEditing, setIsEditing] = useState(false);

  // Text Element Vars
  const [textContent, setTextContent] = useState('');
  const [textFontSize, setTextFontSize] = useState(16);
  const [textFontFamily, setTextFontFamily] = useState('Arial');
  const [textFontWeight, setTextFontWeight] = useState<'normal' | 'bold'>('normal');
  const [textFontStyle, setTextFontStyle] = useState<'normal' | 'italic'>('normal');
  const [textDecoration, setTextDecoration] = useState<'none' | 'underline'>('none');
  const [textColor, setTextColor] = useState('#000000');
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');

  // Sync from Props (Only if not actively editing textual inputs)
  useEffect(() => {
    if (selectedNode && !isEditing) {
      setLabel(selectedNode.data.label);
      setSpecs(selectedNode.data.specs || {});
      setPosition({ x: Math.round(selectedNode.position.x), y: Math.round(selectedNode.position.y) });
      setLocked(!!selectedNode.data.locked);
      setIsCluster(!!selectedNode.data.isCluster);
      setInstanceCount(selectedNode.data.instanceCount);
      setComment(selectedNode.data.comment || '');
      
      // Handle Dimensions (mostly for Groups)
      const w = selectedNode.style?.width ?? selectedNode.width;
      const h = selectedNode.style?.height ?? selectedNode.height;
      setDimensions({ 
          width: typeof w === 'number' ? w : parseInt(String(w || 0), 10), 
          height: typeof h === 'number' ? h : parseInt(String(h || 0), 10) 
      });
    }
  }, [selectedNode, isEditing]);

  useEffect(() => {
    if (selectedEdge && !isEditing) {
      setEdgeType(selectedEdge.data?.type || ConnectionType.HTTP);
      setEdgePort(selectedEdge.data?.port || '');
      setEdgeAnimated(selectedEdge.animated ?? true);
      setEdgeComment(selectedEdge.data?.comment || '');
      setEdgeCustomLabel(selectedEdge.data?.customLabel || '');
      setEdgeLabelVisible(selectedEdge.data?.labelVisible !== false);
      setEdgeLabelPosition(selectedEdge.data?.labelPosition || 'auto');
      setEdgeCustomColor(selectedEdge.data?.customColor || '');
      setEdgeStrokeWidth(selectedEdge.data?.strokeWidth || 'normal');
      setEdgeLineStyle(selectedEdge.data?.lineStyle || 'solid');
      setEdgeOpacity(selectedEdge.data?.opacity !== undefined ? selectedEdge.data.opacity : 1);
      setEdgeLatency(selectedEdge.data?.latency || '');
      setEdgeBandwidth(selectedEdge.data?.bandwidth || '');
      setEdgeTags(selectedEdge.data?.tags || []);
      setEdgeStatus(selectedEdge.data?.status || 'active');
    }
  }, [selectedEdge, isEditing]);

  useEffect(() => {
    if (selectedTextElement && !isEditing) {
      setTextContent(selectedTextElement.text);
      setTextFontSize(selectedTextElement.style.fontSize);
      setTextFontFamily(selectedTextElement.style.fontFamily);
      setTextFontWeight(selectedTextElement.style.fontWeight);
      setTextFontStyle(selectedTextElement.style.fontStyle);
      setTextDecoration(selectedTextElement.style.textDecoration);
      setTextColor(selectedTextElement.style.color);
      setTextAlign(selectedTextElement.style.textAlign);
    }
  }, [selectedTextElement, isEditing]);

  // -- HANDLERS --

  // The main commit function that calls the parent update (and creates a Snapshot)
  const commitNodeChanges = (updates: any) => {
    if (!selectedNode) return;
    
    const nodeUpdates: any = {};
    
    if (updates.label !== undefined) nodeUpdates.data = { ...selectedNode.data, label: updates.label };
    if (updates.specs !== undefined) nodeUpdates.data = { ...selectedNode.data, ...updates.data, specs: updates.specs };
    if (updates.locked !== undefined) nodeUpdates.data = { ...(nodeUpdates.data || selectedNode.data), locked: updates.locked };
    if (updates.isCluster !== undefined) nodeUpdates.data = { ...(nodeUpdates.data || selectedNode.data), isCluster: updates.isCluster };
    if (updates.instanceCount !== undefined) nodeUpdates.data = { ...(nodeUpdates.data || selectedNode.data), instanceCount: updates.instanceCount };
    if (updates.comment !== undefined) nodeUpdates.data = { ...(nodeUpdates.data || selectedNode.data), comment: updates.comment };
    
    if (updates.locked !== undefined) {
        nodeUpdates.draggable = !updates.locked;
        nodeUpdates.connectable = !updates.locked;
    }

    // When converting to cluster, set default dimensions if not already set
    if (updates.isCluster === true && !selectedNode.style?.width && !selectedNode.width) {
        nodeUpdates.style = {
            ...selectedNode.style,
            width: dimensions.width || 130,
            height: dimensions.height || 40
        };
    }

    if (updates.x !== undefined || updates.y !== undefined) {
        nodeUpdates.position = { 
            x: updates.x ?? selectedNode.position.x, 
            y: updates.y ?? selectedNode.position.y 
        };
    }

    if (updates.width !== undefined || updates.height !== undefined) {
        nodeUpdates.style = {
            ...selectedNode.style,
            width: updates.width ?? dimensions.width,
            height: updates.height ?? dimensions.height
        };
    }

    updateNode(selectedNode.id, nodeUpdates);
  };

  const commitEdgeChanges = (updates: any) => {
     if (!selectedEdge) return;
     
     const edgeUpdates: any = {};
     
     if (updates.type !== undefined || updates.port !== undefined || updates.comment !== undefined || 
         updates.customLabel !== undefined || updates.labelVisible !== undefined || updates.labelPosition !== undefined ||
         updates.customColor !== undefined || updates.strokeWidth !== undefined || updates.lineStyle !== undefined || updates.opacity !== undefined ||
         updates.latency !== undefined || updates.bandwidth !== undefined || updates.tags !== undefined || updates.status !== undefined) {
         edgeUpdates.data = { 
             ...selectedEdge.data, 
             type: updates.type ?? edgeType, 
             port: updates.port ?? edgePort,
             comment: updates.comment !== undefined ? updates.comment : edgeComment,
             customLabel: updates.customLabel !== undefined ? updates.customLabel : edgeCustomLabel,
             labelVisible: updates.labelVisible !== undefined ? updates.labelVisible : edgeLabelVisible,
             labelPosition: updates.labelPosition !== undefined ? updates.labelPosition : edgeLabelPosition,
             customColor: updates.customColor !== undefined ? updates.customColor : edgeCustomColor,
             strokeWidth: updates.strokeWidth !== undefined ? updates.strokeWidth : edgeStrokeWidth,
             lineStyle: updates.lineStyle !== undefined ? updates.lineStyle : edgeLineStyle,
             opacity: updates.opacity !== undefined ? updates.opacity : edgeOpacity,
             latency: updates.latency !== undefined ? updates.latency : edgeLatency,
             bandwidth: updates.bandwidth !== undefined ? updates.bandwidth : edgeBandwidth,
             tags: updates.tags !== undefined ? updates.tags : edgeTags,
             status: updates.status !== undefined ? updates.status : edgeStatus
         };
         // Use custom label if provided, otherwise use connection type
         if (updates.type) {
           edgeUpdates.label = updates.customLabel || updates.type;
         } else if (updates.customLabel !== undefined) {
           edgeUpdates.label = updates.customLabel || edgeType;
         }
     }
     
     if (updates.animated !== undefined) {
         edgeUpdates.animated = updates.animated;
     }

     updateEdge(selectedEdge.id, edgeUpdates);
  };

  // -- IMMEDIATE ACTIONS (Toggle/Select) --
  // These are safe to commit immediately as they don't involve rapid typing
  
  const toggleLock = () => { setLocked(!locked); commitNodeChanges({ locked: !locked }); };
  const toggleCluster = () => { setIsCluster(!isCluster); commitNodeChanges({ isCluster: !isCluster }); };
  const changeEdgeType = (val: ConnectionType) => { setEdgeType(val); commitEdgeChanges({ type: val }); };
  const toggleEdgeAnim = () => { setEdgeAnimated(!edgeAnimated); commitEdgeChanges({ animated: !edgeAnimated }); };


  // -- DEFERRED ACTIONS (Text/Number Inputs) --
  // Update local state onChange, but commit onBlur or Enter

  const handleBlurLabel = () => { setIsEditing(false); commitNodeChanges({ label }); };
  const handleBlurX = () => { setIsEditing(false); commitNodeChanges({ x: position.x }); };
  const handleBlurY = () => { setIsEditing(false); commitNodeChanges({ y: position.y }); };
  const handleBlurWidth = () => { setIsEditing(false); commitNodeChanges({ width: dimensions.width }); };
  const handleBlurHeight = () => { setIsEditing(false); commitNodeChanges({ height: dimensions.height }); };
  const handleBlurInstanceCount = () => { setIsEditing(false); commitNodeChanges({ instanceCount: instanceCount && instanceCount > 0 ? instanceCount : undefined }); };
  
  const handleBlurSpec = () => { setIsEditing(false); commitNodeChanges({ specs }); };
  const handleBlurComment = () => { setIsEditing(false); commitNodeChanges({ comment }); };
  const handleBlurEdgePort = () => { setIsEditing(false); commitEdgeChanges({ port: edgePort }); };
  const handleBlurEdgeComment = () => { setIsEditing(false); commitEdgeChanges({ comment: edgeComment }); };
  const handleBlurEdgeCustomLabel = () => { setIsEditing(false); commitEdgeChanges({ customLabel: edgeCustomLabel }); };
  const handleBlurEdgeCustomColor = () => { setIsEditing(false); commitEdgeChanges({ customColor: edgeCustomColor }); };
  const handleBlurEdgeOpacity = () => { setIsEditing(false); commitEdgeChanges({ opacity: edgeOpacity }); };
  const handleBlurEdgeLatency = () => { setIsEditing(false); commitEdgeChanges({ latency: edgeLatency }); };
  const handleBlurEdgeBandwidth = () => { setIsEditing(false); commitEdgeChanges({ bandwidth: edgeBandwidth }); };
  
  const handleAddTag = () => {
    if (edgeTagInput.trim() && !edgeTags.includes(edgeTagInput.trim())) {
      const newTags = [...edgeTags, edgeTagInput.trim()];
      setEdgeTags(newTags);
      setEdgeTagInput('');
      commitEdgeChanges({ tags: newTags });
    }
  };
  
  const handleRemoveTag = (tag: string) => {
    const newTags = edgeTags.filter(t => t !== tag);
    setEdgeTags(newTags);
    commitEdgeChanges({ tags: newTags });
  };

  const handleKeyDown = (e: React.KeyboardEvent, commitFn: () => void) => {
      if (e.key === 'Enter') {
          (e.currentTarget as HTMLElement).blur(); // Triggers onBlur which commits
      }
  };

  // Helper to update specific spec key locally
  const changeSpecLocal = (key: string, val: string) => {
     setSpecs((prev: any) => ({ ...prev, [key]: val }));
  };

  // Collapsed View
  if (isCollapsed) {
    return (
      <div className="w-12 bg-white border-l border-black/10 h-full flex flex-col items-center py-4 relative shrink-0 transition-all duration-300 ease-in-out will-change-width">
        <button 
          onClick={() => setIsCollapsed(false)} 
          className="p-2 hover:bg-gray-100 rounded-md mb-4 text-gray-500 hover:text-black transition-colors"
          title="Expand Panel"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 flex items-center justify-center">
             <div className="rotate-90 whitespace-nowrap text-xs font-bold tracking-widest text-gray-400 uppercase flex items-center gap-2">
                <Settings2 size={14} />
                Properties
             </div>
        </div>
      </div>
    );
  }

  // Expanded View
  return (
    <div className="w-80 bg-white border-l border-black/10 flex flex-col h-full shadow-xl shrink-0 animate-slide-in-right will-change-transform" style={{ animationDuration: '300ms' }}>
      <div className="p-4 border-b border-black/10 flex justify-between items-center bg-white h-[69px]">
        <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsCollapsed(true)} 
              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-black transition-colors"
              title="Collapse Panel"
            >
               <ChevronRight size={18} />
            </button>
            <h2 className="font-bold text-black truncate">
              {selectedNode ? 'Component' : selectedEdge ? 'Connection' : selectedTextElement ? 'Text' : 'Properties'}
            </h2>
        </div>
        {(selectedNode || selectedEdge || selectedTextElement) && (
            <button onClick={onClose} className="text-gray-400 hover:text-black transition-colors" title="Close Selection">
                <X size={18} />
            </button>
        )}
      </div>

      <div className="p-6 space-y-6 flex-1 overflow-y-auto">
        {!selectedNode && !selectedEdge && !selectedTextElement ? (
             <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-50">
                <div className="p-4 bg-gray-50 rounded-full border border-gray-200">
                    <Settings2 size={24} className="text-black" />
                </div>
                <div className="text-sm text-gray-500 max-w-[180px]">Select a component, connection, or text element on the canvas to configure.</div>
             </div>
        ) : (
            <>
                {/* NODE EDITOR */}
                {selectedNode && (
                <>
                    {/* Open Inside Button - Primary Action */}
                    {onOpenInside && (
                    <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-purple-50 border-2 border-purple-300 rounded-lg p-5 shadow-sm">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <FolderOpen size={20} className="text-purple-600" />
                            <h3 className="text-sm font-bold text-gray-800">Nested Architecture</h3>
                        </div>
                        <button
                            onClick={() => onOpenInside(selectedNode.id)}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-150 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.95]"
                            title="Open this component to design its internal architecture. You can add sub-components, connections, and create a detailed breakdown."
                        >
                            <FolderOpen size={18} />
                            <span>Open Inside</span>
                            {selectedNode.data.nestedArchitecture && (
                                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full font-bold ml-1">
                                    {selectedNode.data.nestedArchitecture.nodes.length}
                                </span>
                            )}
                        </button>
                        {selectedNode.data.nestedArchitecture ? (
                            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-purple-700 font-medium bg-purple-100/50 rounded-md py-2 px-3">
                                <Layers size={14} />
                                <span>
                                    {selectedNode.data.nestedArchitecture.nodes.length} component{selectedNode.data.nestedArchitecture.nodes.length !== 1 ? 's' : ''} already inside
                                </span>
                            </div>
                        ) : (
                            <p className="mt-3 text-xs text-gray-600 text-center leading-relaxed">
                                Click to open and design the internal architecture. Add components from the sidebar to create a detailed breakdown.
                            </p>
                        )}
                    </div>
                    )}

                    {/* Basic Info */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Name</label>
                        <input
                            type="text"
                            value={label}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setLabel(e.target.value)}
                            onBlur={handleBlurLabel}
                            onKeyDown={(e) => handleKeyDown(e, handleBlurLabel)}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Type</label>
                        <div className="text-sm text-black font-mono bg-gray-100 px-3 py-2 rounded border border-gray-200">
                            {selectedNode.data.type}
                        </div>
                    </div>

                    {/* Layout Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold text-black flex items-center gap-2">
                                <Move size={14} className="text-gray-500" />
                                Position & Layout
                            </h3>
                            <button 
                                onClick={toggleLock} 
                                className={`p-1.5 rounded transition-colors ${locked ? 'bg-red-50 text-red-600' : 'hover:bg-gray-100 text-gray-400'}`}
                                title={locked ? "Unlock Position" : "Lock Position"}
                            >
                                {locked ? <Lock size={14} /> : <Unlock size={14} />}
                            </button>
                        </div>

                        {/* Cluster Toggle */}
                        {selectedNode.data.type !== ComponentType.GROUP && (
                            <>
                                <div className="flex items-center justify-between pt-2">
                                    <div className="flex flex-col">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Cluster Mode</label>
                                        <span className="text-[10px] text-gray-400 mt-0.5">Render as container</span>
                                    </div>
                                    <div 
                                        onClick={toggleCluster}
                                        className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${isCluster ? 'bg-blue-500' : 'bg-gray-300'}`}
                                    >
                                        <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${isCluster ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </div>
                                </div>
                                
                                {/* Instance Count - only show when cluster mode is active */}
                                {isCluster && (
                                    <div className="space-y-1 pt-2">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Instance Count</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={instanceCount || ''}
                                            placeholder="e.g., 3"
                                            onFocus={() => setIsEditing(true)}
                                            onChange={(e) => setInstanceCount(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                                            onBlur={handleBlurInstanceCount}
                                            onKeyDown={(e) => handleKeyDown(e, handleBlurInstanceCount)}
                                            disabled={locked}
                                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black disabled:bg-gray-50 disabled:text-gray-400"
                                        />
                                        <span className="text-[9px] text-gray-400">Number of instances in this cluster</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Specs Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                    <h3 className="text-sm font-bold text-black flex items-center gap-2">
                        <Maximize size={14} className="text-gray-500" />
                        Specifications
                    </h3>
                    
                    <div className="space-y-2">
                        <label className="text-xs text-gray-500 font-medium">Capacity / CPU</label>
                        <input
                        type="text"
                        placeholder="e.g. 2 vCPU"
                        value={specs.cpu || ''}
                        onFocus={() => setIsEditing(true)}
                        onChange={(e) => changeSpecLocal('cpu', e.target.value)}
                        onBlur={handleBlurSpec}
                        onKeyDown={(e) => handleKeyDown(e, handleBlurSpec)}
                        className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs text-gray-500 font-medium">Memory / RAM</label>
                        <input
                        type="text"
                        placeholder="e.g. 4GB"
                        value={specs.memory || ''}
                        onFocus={() => setIsEditing(true)}
                        onChange={(e) => changeSpecLocal('memory', e.target.value)}
                        onBlur={handleBlurSpec}
                        onKeyDown={(e) => handleKeyDown(e, handleBlurSpec)}
                        className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                        />
                    </div>

                    {(selectedNode.data.type === ComponentType.DATABASE || selectedNode.data.type === ComponentType.CACHE) && (
                        <div className="space-y-2">
                        <label className="text-xs text-gray-500 font-medium">Engine Version</label>
                        <input
                            type="text"
                            placeholder="e.g. PostgreSQL 15"
                            value={specs.engine || ''}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => changeSpecLocal('engine', e.target.value)}
                            onBlur={handleBlurSpec}
                            onKeyDown={(e) => handleKeyDown(e, handleBlurSpec)}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                        />
                        </div>
                    )}
                    </div>

                    {/* Comments Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-black flex items-center gap-2">
                            <MessageSquare size={14} className="text-gray-500" />
                            Comments
                        </h3>
                        <textarea
                            value={comment}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setComment(e.target.value)}
                            onBlur={handleBlurComment}
                            placeholder="Add notes or comments about this component..."
                            rows={3}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none resize-none"
                        />
                    </div>
                </>
                )}

                {/* TEXT ELEMENT EDITOR */}
                {selectedTextElement && (
                <>
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Text Content</label>
                        <textarea
                            value={textContent}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setTextContent(e.target.value)}
                            onBlur={() => {
                                setIsEditing(false);
                                updateTextElement(selectedTextElement.id, { text: textContent });
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    (e.currentTarget as HTMLElement).blur();
                                }
                            }}
                            placeholder="Enter text..."
                            rows={4}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none resize-none"
                        />
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-black">Text Formatting</h3>
                        
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Font Size</label>
                            <input
                                type="number"
                                min="8"
                                max="72"
                                value={textFontSize}
                                onFocus={() => setIsEditing(true)}
                                onChange={(e) => setTextFontSize(parseInt(e.target.value) || 16)}
                                onBlur={() => {
                                    setIsEditing(false);
                                    updateTextElement(selectedTextElement.id, {
                                        style: { ...selectedTextElement.style, fontSize: textFontSize }
                                    });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Font Family</label>
                            <select
                                value={textFontFamily}
                                onChange={(e) => {
                                    setTextFontFamily(e.target.value);
                                    updateTextElement(selectedTextElement.id, {
                                        style: { ...selectedTextElement.style, fontFamily: e.target.value }
                                    });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                            >
                                <option value="Arial">Arial</option>
                                <option value="Helvetica">Helvetica</option>
                                <option value="Times New Roman">Times New Roman</option>
                                <option value="Courier New">Courier New</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Verdana">Verdana</option>
                                <option value="Comic Sans MS">Comic Sans MS</option>
                            </select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Text Color</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={textColor}
                                    onChange={(e) => {
                                        setTextColor(e.target.value);
                                        updateTextElement(selectedTextElement.id, {
                                            style: { ...selectedTextElement.style, color: e.target.value }
                                        });
                                    }}
                                    className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                />
                                <input
                                    type="text"
                                    placeholder="#000000"
                                    value={textColor}
                                    onFocus={() => setIsEditing(true)}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    onBlur={() => {
                                        setIsEditing(false);
                                        updateTextElement(selectedTextElement.id, {
                                            style: { ...selectedTextElement.style, color: textColor }
                                        });
                                    }}
                                    className="flex-1 bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Bold</label>
                            <div 
                                onClick={() => {
                                    const newWeight = textFontWeight === 'bold' ? 'normal' : 'bold';
                                    setTextFontWeight(newWeight);
                                    updateTextElement(selectedTextElement.id, {
                                        style: { ...selectedTextElement.style, fontWeight: newWeight }
                                    });
                                }}
                                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${textFontWeight === 'bold' ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
                                <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${textFontWeight === 'bold' ? 'translate-x-5' : 'translate-x-0'}`} />
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Italic</label>
                            <div 
                                onClick={() => {
                                    const newStyle = textFontStyle === 'italic' ? 'normal' : 'italic';
                                    setTextFontStyle(newStyle);
                                    updateTextElement(selectedTextElement.id, {
                                        style: { ...selectedTextElement.style, fontStyle: newStyle }
                                    });
                                }}
                                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${textFontStyle === 'italic' ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
                                <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${textFontStyle === 'italic' ? 'translate-x-5' : 'translate-x-0'}`} />
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Underline</label>
                            <div 
                                onClick={() => {
                                    const newDecoration = textDecoration === 'underline' ? 'none' : 'underline';
                                    setTextDecoration(newDecoration);
                                    updateTextElement(selectedTextElement.id, {
                                        style: { ...selectedTextElement.style, textDecoration: newDecoration }
                                    });
                                }}
                                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${textDecoration === 'underline' ? 'bg-blue-500' : 'bg-gray-300'}`}
                            >
                                <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${textDecoration === 'underline' ? 'translate-x-5' : 'translate-x-0'}`} />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Text Alignment</label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setTextAlign('left');
                                        updateTextElement(selectedTextElement.id, {
                                            style: { ...selectedTextElement.style, textAlign: 'left' }
                                        });
                                    }}
                                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        textAlign === 'left' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Left
                                </button>
                                <button
                                    onClick={() => {
                                        setTextAlign('center');
                                        updateTextElement(selectedTextElement.id, {
                                            style: { ...selectedTextElement.style, textAlign: 'center' }
                                        });
                                    }}
                                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        textAlign === 'center' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Center
                                </button>
                                <button
                                    onClick={() => {
                                        setTextAlign('right');
                                        updateTextElement(selectedTextElement.id, {
                                            style: { ...selectedTextElement.style, textAlign: 'right' }
                                        });
                                    }}
                                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        textAlign === 'right' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Right
                                </button>
                            </div>
                        </div>
                    </div>
                </>
                )}

                {/* EDGE EDITOR */}
                {selectedEdge && (
                <>
                    <div className="flex justify-center py-6">
                    <div className="bg-gray-50 p-4 rounded-full border border-gray-200">
                        <ArrowRight className="text-black" size={24} />
                    </div>
                    </div>

                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Protocol</label>
                        <select
                            value={edgeType}
                            onChange={(e) => changeEdgeType(e.target.value as ConnectionType)}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                        >
                            {CONNECTION_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Custom Label</label>
                        <input
                            type="text"
                            placeholder="e.g. API Request"
                            value={edgeCustomLabel}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setEdgeCustomLabel(e.target.value)}
                            onBlur={handleBlurEdgeCustomLabel}
                            onKeyDown={(e) => handleKeyDown(e, handleBlurEdgeCustomLabel)}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                        />
                        <p className="text-[10px] text-gray-400">Leave empty to use connection type as label</p>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <div className="flex flex-col">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Show Label</label>
                            <span className="text-[10px] text-gray-400 mt-0.5">Display connection label</span>
                        </div>
                        <div 
                            onClick={() => {
                                setEdgeLabelVisible(!edgeLabelVisible);
                                commitEdgeChanges({ labelVisible: !edgeLabelVisible });
                            }}
                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${edgeLabelVisible ? 'bg-blue-500' : 'bg-gray-300'}`}
                        >
                            <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${edgeLabelVisible ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                    </div>

                    {edgeLabelVisible && (
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Label Position</label>
                            <select
                                value={edgeLabelPosition}
                                onChange={(e) => {
                                    setEdgeLabelPosition(e.target.value as 'auto' | 'top' | 'bottom' | 'left' | 'right');
                                    commitEdgeChanges({ labelPosition: e.target.value as 'auto' | 'top' | 'bottom' | 'left' | 'right' });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                            >
                                <option value="auto">Auto</option>
                                <option value="top">Top</option>
                                <option value="bottom">Bottom</option>
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                    )}

                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Port</label>
                        <input
                            type="text"
                            placeholder="e.g. 5432"
                            value={edgePort}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setEdgePort(e.target.value)}
                            onBlur={() => {
                                setIsEditing(false);
                                // Validate port
                                if (edgePort) {
                                    const portNum = parseInt(edgePort, 10);
                                    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                                        alert(`Invalid port number: ${edgePort}. Must be between 1 and 65535.`);
                                        return;
                                    }
                                }
                                commitEdgeChanges({ port: edgePort });
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    (e.currentTarget as HTMLElement).blur();
                                }
                            }}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                        />
                        {edgePort && (() => {
                            const portNum = parseInt(edgePort, 10);
                            const isValid = !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
                            return !isValid ? (
                                <p className="text-[10px] text-red-600">Port must be between 1 and 65535</p>
                            ) : null;
                        })()}
                    </div>
                    
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-black flex items-center gap-2">
                            Styling
                        </h3>
                        
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Custom Color</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={edgeCustomColor || '#000000'}
                                    onChange={(e) => setEdgeCustomColor(e.target.value)}
                                    onBlur={handleBlurEdgeCustomColor}
                                    className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                />
                                <input
                                    type="text"
                                    placeholder="#000000"
                                    value={edgeCustomColor}
                                    onFocus={() => setIsEditing(true)}
                                    onChange={(e) => setEdgeCustomColor(e.target.value)}
                                    onBlur={handleBlurEdgeCustomColor}
                                    onKeyDown={(e) => handleKeyDown(e, handleBlurEdgeCustomColor)}
                                    className="flex-1 bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                                />
                                {edgeCustomColor && (
                                    <button
                                        onClick={() => {
                                            setEdgeCustomColor('');
                                            commitEdgeChanges({ customColor: '' });
                                        }}
                                        className="text-xs text-gray-500 hover:text-red-600"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Line Thickness</label>
                            <select
                                value={edgeStrokeWidth}
                                onChange={(e) => {
                                    setEdgeStrokeWidth(e.target.value as 'thin' | 'normal' | 'thick');
                                    commitEdgeChanges({ strokeWidth: e.target.value as 'thin' | 'normal' | 'thick' });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                            >
                                <option value="thin">Thin</option>
                                <option value="normal">Normal</option>
                                <option value="thick">Thick</option>
                            </select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Line Style</label>
                            <select
                                value={edgeLineStyle}
                                onChange={(e) => {
                                    setEdgeLineStyle(e.target.value as 'solid' | 'dashed' | 'dotted');
                                    commitEdgeChanges({ lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                            >
                                <option value="solid">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                            </select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Opacity: {Math.round(edgeOpacity * 100)}%</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={edgeOpacity}
                                onChange={(e) => setEdgeOpacity(parseFloat(e.target.value))}
                                onMouseUp={handleBlurEdgeOpacity}
                                onKeyUp={handleBlurEdgeOpacity}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Animated</label>
                        <div 
                            onClick={toggleEdgeAnim}
                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${edgeAnimated ? 'bg-green-500' : 'bg-gray-300'}`}
                        >
                            <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out ${edgeAnimated ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-500 font-mono mt-4">
                        <p>ID: {selectedEdge.id}</p>
                        <p className="mt-1">From: {selectedEdge.source}</p>
                        <p>To: {selectedEdge.target}</p>
                    </div>

                    {/* Metadata Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-black flex items-center gap-2">
                            Metadata
                        </h3>
                        
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status</label>
                            <select
                                value={edgeStatus}
                                onChange={(e) => {
                                    setEdgeStatus(e.target.value as 'active' | 'inactive' | 'degraded');
                                    commitEdgeChanges({ status: e.target.value as 'active' | 'inactive' | 'degraded' });
                                }}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none cursor-pointer"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="degraded">Degraded</option>
                            </select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Latency</label>
                            <input
                                type="text"
                                placeholder="e.g. 50ms, 100ms"
                                value={edgeLatency}
                                onFocus={() => setIsEditing(true)}
                                onChange={(e) => setEdgeLatency(e.target.value)}
                                onBlur={handleBlurEdgeLatency}
                                onKeyDown={(e) => handleKeyDown(e, handleBlurEdgeLatency)}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Bandwidth</label>
                            <input
                                type="text"
                                placeholder="e.g. 1Gbps, 100Mbps"
                                value={edgeBandwidth}
                                onFocus={() => setIsEditing(true)}
                                onChange={(e) => setEdgeBandwidth(e.target.value)}
                                onBlur={handleBlurEdgeBandwidth}
                                onKeyDown={(e) => handleKeyDown(e, handleBlurEdgeBandwidth)}
                                className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tags</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Add tag..."
                                    value={edgeTagInput}
                                    onChange={(e) => setEdgeTagInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddTag();
                                        }
                                    }}
                                    className="flex-1 bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none"
                                />
                                <button
                                    onClick={handleAddTag}
                                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-black transition-colors"
                                >
                                    Add
                                </button>
                            </div>
                            {edgeTags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {edgeTags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                                        >
                                            {tag}
                                            <button
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:text-blue-900"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Comments Section for Edge */}
                    <div className="space-y-4 pt-4 border-t border-gray-100 mt-4">
                        <h3 className="text-sm font-bold text-black flex items-center gap-2">
                            <MessageSquare size={14} className="text-gray-500" />
                            Comments
                        </h3>
                        <textarea
                            value={edgeComment}
                            onFocus={() => setIsEditing(true)}
                            onChange={(e) => setEdgeComment(e.target.value)}
                            onBlur={handleBlurEdgeComment}
                            placeholder="Add notes or comments about this connection..."
                            rows={3}
                            className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm text-black focus:ring-1 focus:ring-black focus:border-black focus:outline-none resize-none"
                        />
                    </div>
                </>
                )}
            </>
        )}
      </div>

      {(selectedNode || selectedEdge || selectedTextElement) && (
        <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
            <button 
                onClick={selectedTextElement ? onDeleteTextElement : onDelete}
                className="w-full flex items-center justify-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-gray-200 hover:border-red-200 py-2 px-4 rounded-md transition-all duration-150 font-medium text-sm hover:scale-[1.02] active:scale-[0.95]"
            >
                <Trash2 size={16} />
                Delete {selectedNode ? 'Component' : selectedEdge ? 'Connection' : 'Text'}
            </button>
        </div>
      )}
    </div>
  );
};

export default PropertiesPanel;